
import {
  Component, Prop, Ref, Vue,
} from 'vue-property-decorator'
import CellFormat from '@/components/TableGroup/CellFormat.vue'
import SelectPicker from '@/components/SelectPicker/SelectPicker.vue'
import ViewModel from '@/models/ViewModel'
import { uniqueId } from 'lodash'
import DataTableField from '../DataTable/DataTableField'

@Component({
  components: {
    CellFormat,
    SelectPicker,
  },
})
/**
 * TODO
 * - Add sorting
 *
 * This table group component is used to display a table with a group of items.
 * So add the normal table functions and props in here.
 */
export default class TableGroup extends ViewModel {
  @Prop({ required: true })
  public items!: any

  @Prop({ required: true })
  public subHeaders!: any[]

  @Prop()
  public caption!: string

  @Prop({
    default: () => ({
      hover: true,
      small: true,
      captionTop: true,
      responsive: true,
      bordered: true,
    }),
  })
  public tableProps!: any

  // Bootstrap table reference
  @Ref()
  public readonly dataTable!: any

  // Reference DIV for loading dynamic components
  @Ref()
  public readonly container!: any

  @Prop({ default: () => [] })
  public readonly fields!: Array<DataTableField>

  // Default sorting method
  @Prop({ default: () => null })
  public readonly sortBy!: string | null

  // Default sorting method
  @Prop({ default: () => true })
  public readonly sortDesc!: boolean

  // Default sorting method
  @Prop({ default: () => 25 })
  public readonly perPage!: number

  @Prop({ default: () => null })
  public readonly currentPage!: any

  // Checked Items (Sync)
  @Prop({ default: () => [] })
  public readonly checked!: Array<string>

  // Pormise that return table records
  @Prop({ default: () => [] })
  public readonly rows!: Promise<any>

  // Total amount of records
  @Prop({ default: () => 0 })
  public readonly records!: number

  // Loading indicator
  @Prop({ default: () => false })
  public readonly loading!: boolean

  // Table search query
  @Prop({ default: () => [] })
  public readonly query!: any

  // Field Filters
  @Prop({ default: () => false })
  public fieldFilters!: any

  // Field Filters
  @Prop({ default: () => false })
  public noPagination!: boolean

  @Prop({ default: () => false })
  public footClone!: boolean

  @Prop({ default: () => false })
  public noFooterSorting!: boolean

  @Prop({ default: () => false })
  public noOverflowAuto!: boolean

  @Prop({ default: () => false })
  public stickyHeader!: boolean

  @Prop({ default: () => false })
  public allowAll!: boolean

  @Prop({ default: () => false })
  public filterSetsPage!: boolean

  @Prop({ default: () => false })
  public notRequest!: boolean

  public uuid: string = uniqueId()

  public collapsed_groups: any = {}

  public loaded_items: any = []

  public data_loading: boolean = true

  public filterTimeout: any = null

  public get records_text(): string {
    if (this.records === 0) {
      return ''
    }

    if (this.page_size === 'all' || this.page_size === 0) {
      return `Showing all ${this.records} records`
    }

    let start = (this.page - 1) * this.recordsPerPage + 1
    let end = this.page * this.recordsPerPage

    if (end > this.records) {
      end = this.records
    }

    return `${start} - ${end} of ${this.records}`
  }

  // Check if all rows are selected
  public get allSelected(): boolean {
    return this.dataTable && this.dataTable.localItems.length > 0
      ? this.checked.length === this.dataTable.localItems.length
      : false
  }

  // Check or uncheck all records
  public set allSelected(checked: boolean) {
    this.local_checked = checked ? this.dataTable.localItems.map((r: any) => r.id) : []
  }

  // Check if datatable has checked records but not all
  public get indeterminate(): boolean {
    return (
      this.checked.length > 0
      && this.dataTable
      && this.checked.length < this.dataTable.localItems.length
    )
  }

  // Return active field filters
  public get localFieldFilters() {
    return this.fieldFilters
  }

  // Upate parent field filters
  public set localFieldFilters(value) {
    this.$emit('update:fieldFilters', value)
  }

  // Default table pagination options
  public get pagination_options(): any {
    let ret = [
      { name: '10', value: '10' },
      { name: '25', value: '25' },
      { name: '50', value: '50' },
      { name: '100', value: '100' },
    ]

    if (this.allowAll !== false) {
      ret.push({ name: 'All', value: 'all' })
    }

    return ret
  }

  // Returns checked prop to prevent updating prop
  public get local_checked(): Array<string> {
    if (!this.checked) return []
    return this.checked
  }

  // Update parent checked prop
  public set local_checked(value: Array<string>) {
    this.$emit('update:checked', value)
  }

  // Sort by column
  public sort_by: string = 'created_at'

  // Inital page size
  public page_size: any = 25

  public get recordsPerPage(): number {
    return this.noPagination ? 50000 : this.page_size
  }

  // Inital page
  public page: number = 1

  // Sort DESC or ASC
  public sort_desc: boolean = true

  // Refresh datatable
  public refresh() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout)
    }

    this.dataTable.refresh()
  }

  // Clear all selection
  public resetSelection(v: number) {
    this.local_checked = []
    this.allSelected = false
    this.$emit('page-changed', v)
  }

  // Toggle checkbox selection
  public toggleSelectAll(value: boolean) {
    this.allSelected = value
  }

  /**
   * Runs on datatable action click.
   *
   * If action has callable, execute callable and if it returns a promise, refresh datatable on resolve.
   * If no callable, emit event name
   */
  public triggerAction(event: string, item: any) {
    // @ts-ignore
    let action = this.fields
      .find((f: any) => f.key === 'action')
      .actions.find((a: any) => a.event === event)

    if (action && action.action !== undefined && action.action !== null) {
      let p = action.action(item, this.dataTable, this.container)

      if (p instanceof Promise) {
        p.then((refresh: boolean) => {
          if (refresh) {
            this.refresh()
          }
        })
      }
    }
    this.$emit(event, item)
  }

  public get local_fields() {
    return this.fields || []
  }

  public get local_items() {
    return this.loaded_items || []
  }

  public get local_sub_headers() {
    return this.subHeaders || []
  }

  public toggle_columns(key: string) {}

  public isLastIndex(array: any[], index: number, n: any) {
    return array.filter(f => f.key.includes(n.key) && f.show).length - 1 === index
  }

  public splitKeys(key: string) {
    return key.split('_')
  }

  public getValue(arr: any, key: string) {
    return arr.find((f: any) => f.key === key).value
  }

  public getColumnTotal(key: string) {
    const keys = this.splitKeys(key)
    const property = keys[0]
    const propItemKey = keys[1]

    const field = this.local_sub_headers.find((f: any) => f.key === key)

    if (field.customTotal) {
      const payload: any[] = []

      this.loaded_items.forEach((item: any) => {
        let obj: any = {}
        item[property].forEach((f: any) => {
          obj[f.key] = f.value
        })
        payload.push(obj)
      })

      return field.customTotal(payload)
    }

    return this.local_items.reduce((acc: number, item: any) => {
      const index = item[property].findIndex((f: any) => f.key === propItemKey)

      return acc + item[property][index].value
    }, 0)
  }

  public created() {
    if (this.currentPage) {
      this.page = this.currentPage
    }
    // If default sort is set, copy to local var
    if (this.sortBy) {
      this.sort_by = this.sortBy
    }

    this.sort_desc = this.sortDesc

    this.page_size = this.perPage
  }

  public mounted() {
    if (typeof this.items === 'function') {
      this.items().then((res: any) => {
        this.loaded_items = res
        this.data_loading = false
      })
    } else {
      this.loaded_items = this.items
    }
  }

  public changePerPage(page: any) {
    if (this.loading || this.data_loading) return
    if (page.hasOwnProperty('value')) {
      this.$emit('update:perPage', page.value)

      setTimeout(() => {
        this.resetSelection(this.page)
      }, 100)
    }
  }
}
